import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

function triggerAIAgent() {
  window.open('https://wa.me/201099289280', '_blank');
}

interface FaqItem {
  qKey: string;
  aKey: string;
}

const faqItems: FaqItem[] = [
  { qKey: 'faq.q1', aKey: 'faq.a1' },
  { qKey: 'faq.q2', aKey: 'faq.a2' },
  { qKey: 'faq.q3', aKey: 'faq.a3' },
  { qKey: 'faq.q4', aKey: 'faq.a4' },
  { qKey: 'faq.q5', aKey: 'faq.a5' },
  { qKey: 'faq.q6', aKey: 'faq.a6' },
  { qKey: 'faq.q7', aKey: 'faq.a7' },
  { qKey: 'faq.q8', aKey: 'faq.a8' },
  { qKey: 'faq.q9', aKey: 'faq.a9' },
  { qKey: 'faq.q10', aKey: 'faq.a10' },
];

export default function FaqPage() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const seoTitle = `${t('faq.title')} — Texio ERP`;

  return (
    <div className="min-h-screen bg-background-50">
      <title>{seoTitle}</title>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          'mainEntity': faqItems.map((item) => ({
            '@type': 'Question',
            'name': t(item.qKey),
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': t(item.aKey),
            },
          })),
        }) }}
      />

      <Navbar />

      <main>
        <article className="pt-24 md:pt-32 pb-16 md:pb-24">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-3xl mx-auto">
              {/* Header */}
              <div className="mb-12 md:mb-16">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground-950 mb-3">
                  {t('faq.title')}
                </h1>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                  {t('faq.subtitle')}
                </p>
                <p className="text-xs text-foreground-400 mt-3">
                  {t('faq.last_updated')}
                </p>
              </div>

              {/* FAQ Accordion */}
              <div className="flex flex-col gap-3">
                {faqItems.map((item, index) => {
                  const isOpen = openIndex === index;

                  return (
                    <div
                      key={item.qKey}
                      className={`rounded-xl border transition-all duration-300 overflow-hidden ${
                        isOpen
                          ? 'border-primary-200 bg-primary-50/30'
                          : 'border-background-200/70 bg-background-50 hover:border-background-300/60'
                      }`}
                    >
                      <button
                        onClick={() => setOpenIndex(isOpen ? null : index)}
                        className="w-full flex items-start justify-between gap-4 px-5 py-4 md:px-6 md:py-5 text-left cursor-pointer group"
                        aria-expanded={isOpen}
                      >
                        <h2 className="text-sm md:text-base font-semibold text-foreground-900 pr-2 leading-snug">
                          {t(item.qKey)}
                        </h2>
                        <span className="shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center rounded-full border border-foreground-200 text-foreground-400 group-hover:border-foreground-400 transition-colors duration-200">
                          <i
                            className={`text-xs transition-transform duration-300 ${
                              isOpen ? 'ri-subtract-line rotate-180' : 'ri-add-line'
                            }`}
                          ></i>
                        </span>
                      </button>
                      <div
                        className={`overflow-hidden transition-all duration-300 ${
                          isOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="px-5 pb-5 md:px-6 md:pb-6 text-sm md:text-base text-foreground-600 leading-relaxed">
                          <p>{t(item.aKey)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Bottom CTA */}
              <div className="mt-14 md:mt-20 p-8 md:p-10 bg-foreground-950 rounded-2xl text-center">
                <h3 className="text-xl md:text-2xl font-bold text-background-50 mb-3">
                  {t('faq.cta_title')}
                </h3>
                <p className="text-sm md:text-base text-background-50/55 max-w-lg mx-auto mb-6 leading-relaxed">
                  {t('faq.cta_text')}
                </p>
                <button
                  onClick={triggerAIAgent}
                  className="inline-flex items-center gap-2 bg-background-50 text-foreground-950 px-6 py-3 rounded-full text-sm font-semibold hover:bg-background-50/90 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  {t('faq.cta_button')}
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </button>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}